import * as theme from '../theme';

import styled from 'styled-components';

const NoticeAtCollection = () => {
    return (
        <Wrapper>
            <Title>California Notice at Collection of Personal Information</Title>
            <Subtitle>Peklava LLC dba RateZip</Subtitle>

            <Paragraph>
                This California Notice at Collection of Personal Information
                (&ldquo;Notice&rdquo;) supplements the RateZip Privacy and
                Security Policy and applies to California residents. We provide
                this Notice at or before the point where we collect personal
                information so that California consumers understand the
                categories of personal information we collect, the purposes for
                which we use it, how long we retain it, and the rights available
                under the California Consumer Privacy Act, as amended by the
                California Privacy Rights Act (&ldquo;CCPA/CPRA&rdquo;).
            </Paragraph>

            <SectionHeading>Summary</SectionHeading>
            <Bulletlist>
                <li>
                    RateZip collects personal information that you provide through
                    our quote request forms and information automatically collected
                    when you use our website.
                </li>
                <li>
                    We use this information to process your request, match you with
                    relevant lenders or service providers, communicate with you,
                    maintain consent and compliance records, improve our website, and
                    conduct marketing and analytics.
                </li>
                <li>
                    RateZip may disclose, sell, or share certain personal information
                    with lenders, mortgage partners, marketing partners, affiliates,
                    service providers, analytics providers, and advertising
                    technology providers as described below.
                </li>
                <li>
                    You may exercise your California privacy rights by contacting us
                    at{' '}
                    <a href='mailto:privacy@ratezip.com'>privacy@ratezip.com</a> or
                    by mail at the address listed below.
                </li>
            </Bulletlist>

            <SectionHeading>
                Categories of Personal Information We Collect
            </SectionHeading>
            <Paragraph>
                The categories below are based on the California statutory
                categories. We collect only the information that you provide,
                that is generated by your interaction with our website, or that
                is needed to process your request and operate our business.
            </Paragraph>

            <TableWrapper>
                <Table>
                    <thead>
                        <tr>
                            <Th>Category</Th>
                            <Th>Examples Collected</Th>
                            <Th>Sources</Th>
                            <Th>Business/Commercial Purposes</Th>
                            <Th>Sold/Shared or Disclosed?</Th>
                            <Th>Retention Criteria</Th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <Td><strong>Identifiers</strong></Td>
                            <Td>
                                Name, mailing address, property address, email
                                address, telephone number, IP address, online
                                identifiers, device identifiers,
                                LeadID/TrustedForm identifiers, and similar
                                contact or technical identifiers.
                            </Td>
                            <Td>
                                Directly from you; automatically from your
                                browser/device; lead verification and
                                consent-record providers.
                            </Td>
                            <Td>
                                Process quote requests; match you with lenders
                                or partners; contact you about your request;
                                verify consent; prevent fraud; maintain records;
                                marketing, analytics, and website optimization.
                            </Td>
                            <Td>
                                May be disclosed to service providers and
                                partners. May be sold or shared with lenders,
                                mortgage partners, marketing partners,
                                affiliates, and advertising/analytics partners,
                                subject to your opt-out rights.
                            </Td>
                            <Td>
                                Retained for as long as reasonably necessary to
                                process your request, maintain compliance and
                                consent records, handle disputes, meet legal
                                obligations, and support suppression/opt-out
                                records. Lead and consent records may be
                                retained for multiple years where needed for
                                legal or compliance purposes.
                            </Td>
                        </tr>
                        <tr>
                            <Td>
                                <strong>
                                    California Customer Records / Financial or
                                    Property-Related Information
                                </strong>
                            </Td>
                            <Td>
                                Name, address, telephone number, email address,
                                property information, home value, mortgage
                                balance, loan or quote request details, and
                                other information you provide in connection with
                                a mortgage or rate inquiry. We do not request
                                Social Security numbers, bank account
                                credentials, credit card numbers, passport
                                numbers, or driver&rsquo;s license numbers
                                unless a specific form expressly says otherwise.
                            </Td>
                            <Td>
                                Directly from you through web forms or
                                communications with us; inferences from form
                                entries needed to process the request.
                            </Td>
                            <Td>
                                Evaluate and route your request; provide or
                                facilitate quotes; connect you with lenders or
                                providers; comply with licensing, recordkeeping,
                                and legal obligations; quality assurance;
                                analytics and business reporting.
                            </Td>
                            <Td>
                                May be disclosed to service providers and
                                partners. May be sold or shared with lenders,
                                mortgage partners, marketing partners, and
                                affiliates in connection with your request,
                                subject to your opt-out rights.
                            </Td>
                            <Td>
                                Retained as needed for the purposes above,
                                including recordkeeping, legal, compliance,
                                audit, and dispute-resolution purposes. We do
                                not retain personal information longer than
                                reasonably necessary for the disclosed purposes
                                unless required or permitted by law.
                            </Td>
                        </tr>
                        <tr>
                            <Td>
                                <strong>
                                    Protected Classification Characteristics
                                </strong>
                            </Td>
                            <Td>
                                Age or date of birth range, senior eligibility
                                information, marital status, veteran or military
                                status, or similar information, only if
                                requested by a form or voluntarily provided by
                                you for a product eligibility or quote purpose.
                            </Td>
                            <Td>Directly from you.</Td>
                            <Td>
                                Determine product eligibility; route your
                                inquiry to appropriate lenders or partners;
                                comply with applicable law; quality assurance
                                and reporting.
                            </Td>
                            <Td>
                                May be disclosed to service providers and
                                partners. May be sold or shared with lenders,
                                mortgage partners, marketing partners, and
                                affiliates in connection with your request,
                                subject to your opt-out rights.
                            </Td>
                            <Td>
                                Retained only as reasonably necessary for quote
                                processing, compliance, audit, legal, and
                                dispute-resolution purposes.
                            </Td>
                        </tr>
                        <tr>
                            <Td>
                                <strong>Commercial Information</strong>
                            </Td>
                            <Td>
                                Products or services considered, quote requests
                                submitted, partner or lender referrals,
                                campaign/source information, and records of
                                interactions with RateZip or receiving partners.
                            </Td>
                            <Td>
                                Directly from you; automatically through website
                                interactions; from our service providers,
                                partners, and analytics tools.
                            </Td>
                            <Td>
                                Provide requested services; measure campaign
                                performance; improve website and partner
                                matching; reporting; marketing and analytics.
                            </Td>
                            <Td>
                                May be disclosed to service providers and
                                partners. May be sold or shared with lenders,
                                mortgage partners, marketing partners,
                                affiliates, and advertising/analytics partners,
                                subject to your opt-out rights.
                            </Td>
                            <Td>
                                Retained as reasonably necessary for business
                                operations, reporting, compliance, and legal
                                purposes.
                            </Td>
                        </tr>
                        <tr>
                            <Td>
                                <strong>
                                    Internet or Other Electronic Network
                                    Activity
                                </strong>
                            </Td>
                            <Td>
                                Browsing information, landing page, referring
                                URL, form interactions, click identifiers,
                                cookie identifiers, device/browser information,
                                IP address, pages viewed, and interactions with
                                our website, advertisements, or emails.
                            </Td>
                            <Td>
                                Automatically from your browser/device; cookies,
                                pixels, analytics tools, advertising platforms,
                                and similar technologies.
                            </Td>
                            <Td>
                                Operate and secure the website; measure traffic
                                and campaign performance; detect fraud; remember
                                preferences; analytics; marketing; advertising
                                measurement and optimization.
                            </Td>
                            <Td>
                                May be disclosed to service providers and
                                analytics providers. May be shared with
                                advertising technology partners for
                                cross-context behavioral advertising, subject to
                                your opt-out rights.
                            </Td>
                            <Td>
                                Retained according to business need and tool
                                configuration, then deleted, aggregated, or
                                de-identified when no longer reasonably
                                necessary. Cookie durations vary by technology.
                            </Td>
                        </tr>
                        <tr>
                            <Td>
                                <strong>Geolocation Data</strong>
                            </Td>
                            <Td>
                                Approximate location derived from IP address,
                                zip code, property address, or mailing address.
                                We do not intentionally collect precise
                                geolocation unless expressly disclosed at the
                                point of collection.
                            </Td>
                            <Td>
                                Directly from you; automatically from IP address
                                or device/network information.
                            </Td>
                            <Td>
                                Determine service availability; route your
                                request; comply with licensing/state
                                restrictions; analytics and fraud prevention.
                            </Td>
                            <Td>
                                May be disclosed to service providers and
                                partners. May be sold or shared with lenders,
                                mortgage partners, marketing partners,
                                affiliates, and advertising/analytics partners
                                where applicable, subject to your opt-out
                                rights.
                            </Td>
                            <Td>
                                Retained as reasonably necessary for request
                                processing, compliance, analytics, and legal
                                purposes.
                            </Td>
                        </tr>
                        <tr>
                            <Td>
                                <strong>Inferences</strong>
                            </Td>
                            <Td>
                                Lead scoring, product interest, eligibility
                                indicators, likelihood of interest, or similar
                                analytics derived from information you provide
                                and your interactions with our website.
                            </Td>
                            <Td>
                                Created by RateZip or service providers from
                                form data, campaign data, and website
                                interactions.
                            </Td>
                            <Td>
                                Improve partner matching; optimize marketing;
                                measure performance; prevent fraud; improve
                                website experience and business operations.
                            </Td>
                            <Td>
                                May be disclosed to service providers and
                                partners. May be sold or shared where used for
                                marketing, partner routing, or advertising
                                purposes, subject to your opt-out rights.
                            </Td>
                            <Td>
                                Retained as reasonably necessary for analytics,
                                reporting, optimization, compliance, and
                                business purposes, or maintained in
                                aggregated/de-identified form.
                            </Td>
                        </tr>
                    </tbody>
                </Table>
            </TableWrapper>

            <SectionHeading>
                Categories We Do Not Intentionally Collect
            </SectionHeading>
            <Paragraph>
                Unless a specific form or later interaction expressly states
                otherwise, RateZip does not intentionally collect biometric
                information, non-public education records, precise geolocation,
                genetic data, contents of private communications, account log-in
                credentials, Social Security numbers, driver&rsquo;s license
                numbers, passport numbers, or payment card/bank account
                credentials.
            </Paragraph>

            <SectionHeading>Sensitive Personal Information</SectionHeading>
            <Paragraph>
                RateZip does not use or disclose sensitive personal information
                for purposes that would require a right to limit under the
                CCPA/CPRA unless we provide a separate notice and right to limit
                at or before collection. Please do not submit sensitive personal
                information that is not requested by the applicable form.
            </Paragraph>

            <SectionHeading>
                Purposes for Collection, Use, Disclosure, Sale, and Sharing
            </SectionHeading>
            <Bulletlist>
                <li>
                    To fulfill or meet the reason you provided the information,
                    including responding to a quote or information request.
                </li>
                <li>
                    To match, route, disclose, sell, or share your inquiry with
                    lenders, mortgage partners, marketing partners, affiliates,
                    or other providers that may contact you about products or
                    services relevant to your request.
                </li>
                <li>
                    To contact you by phone, text, email, mail, or other means
                    where permitted by law and consistent with any consent you
                    provide.
                </li>
                <li>
                    To maintain records of your request, consent, opt-out
                    preferences, and communications.
                </li>
                <li>
                    To operate, maintain, secure, debug, and improve our website,
                    databases, technology assets, and services.
                </li>
                <li>
                    For analytics, marketing, advertising measurement, campaign
                    optimization, fraud prevention, quality assurance, testing,
                    research, and product development.
                </li>
                <li>
                    To comply with applicable law, licensing obligations, law
                    enforcement requests, court orders, governmental regulations,
                    and legal or regulatory recordkeeping requirements.
                </li>
                <li>
                    For any other purpose disclosed to you at the time of
                    collection or with your consent.
                </li>
            </Bulletlist>

            <SectionHeading>
                Sale or Sharing of Personal Information
            </SectionHeading>
            <Paragraph>
                Under California law, &ldquo;sell&rdquo; and &ldquo;share&rdquo;
                are defined broadly. RateZip may sell personal information by
                providing lead or inquiry information to lenders, mortgage
                partners, marketing partners, affiliates, or other providers in
                connection with your request. RateZip may also share personal
                information with advertising and analytics partners for
                cross-context behavioral advertising or advertising measurement.
            </Paragraph>
            <Paragraph>
                You have the right to opt out of the sale or sharing of your
                personal information. To exercise this right, email{' '}
                <a href='mailto:privacy@ratezip.com'>privacy@ratezip.com</a>,
                write to us at the address below, or use the &ldquo;Do Not Sell
                or Share My Personal Information&rdquo; link made available on
                our website. We also honor browser-based opt-out preference
                signals, such as Global Privacy Control, where required by
                applicable law and technically feasible.
            </Paragraph>

            <SectionHeading>Retention of Personal Information</SectionHeading>
            <Paragraph>
                We retain personal information only for as long as reasonably
                necessary and proportionate to achieve the purposes disclosed in
                this Notice, unless a longer retention period is required or
                permitted by law. Retention periods vary depending on the
                category of information and the purpose for collection.
            </Paragraph>
            <Bulletlist>
                <li>
                    Lead, inquiry, consent, and compliance records may be
                    retained for multiple years as needed to demonstrate consent,
                    resolve disputes, comply with legal obligations, and support
                    audit or regulatory requirements.
                </li>
                <li>
                    Website analytics, cookie, device, and campaign performance
                    data are retained according to business need and the settings
                    of the applicable analytics or advertising tool, then
                    deleted, aggregated, or de-identified when no longer
                    reasonably necessary.
                </li>
                <li>
                    Opt-out, suppression, and privacy request records may be
                    retained as needed to comply with and document our response
                    to your request.
                </li>
                <li>
                    We may retain de-identified, aggregated, or anonymized
                    information that cannot reasonably be used to identify you.
                </li>
            </Bulletlist>

            <SectionHeading>Your California Privacy Rights</SectionHeading>
            <Paragraph>
                Subject to applicable exceptions, California residents may have
                the following rights:
            </Paragraph>
            <Bulletlist>
                <li>
                    <strong>Right to Know/Access:</strong> You may request that
                    we disclose the categories and specific pieces of personal
                    information we collected about you, the categories of
                    sources, purposes, categories of third parties, and
                    sale/sharing/disclosure information.
                </li>
                <li>
                    <strong>Right to Delete:</strong> You may request that we
                    delete personal information we collected from you, subject to
                    legal exceptions.
                </li>
                <li>
                    <strong>Right to Correct:</strong> You may request that we
                    correct inaccurate personal information we maintain about
                    you.
                </li>
                <li>
                    <strong>Right to Opt Out of Sale or Sharing:</strong> You may
                    direct us not to sell or share your personal information.
                </li>
                <li>
                    <strong>
                        Right to limit Use of Sensitive Personal Information:
                    </strong>{' '}
                    If we use sensitive personal information for purposes that
                    trigger this right, you may direct us to limit that use.
                </li>
                <li>
                    <strong>Right to Non-Discrimination:</strong> We will not
                    discriminate against you for exercising your CCPA/CPRA
                    rights.
                </li>
            </Bulletlist>

            <SectionHeading>
                How to Submit a California Privacy Request
            </SectionHeading>
            <Paragraph>
                To exercise your California privacy rights, please submit a
                verifiable consumer request by either:
            </Paragraph>
            <Bulletlist>
                <li>
                    Emailing us at{' '}
                    <a href='mailto:privacy@ratezip.com'>privacy@ratezip.com</a>
                </li>
                <li>
                    Mailing a written request to: Peklava LLC dba RateZip, 59
                    Grove Street, Unit 2J, New Canaan, CT 06840
                </li>
                <li>
                    Using the &ldquo;Do Not Sell or Share My Personal
                    Information&rdquo; link on our website for opt-out requests,
                    when available.
                </li>
            </Bulletlist>
            <Paragraph>
                Only you, or a person legally authorized to act on your behalf,
                may make a verifiable consumer request related to your personal
                information. We may need to verify your identity or authority
                before responding. We will use information provided in a privacy
                request only to verify and respond to that request, unless you
                authorize another use.
            </Paragraph>

            <SectionHeading>Response Timing</SectionHeading>
            <Paragraph>
                We endeavor to respond to verifiable consumer requests within
                forty-five (45) days of receipt. If we require more time, we will
                inform you of the reason and extension period in writing, as
                permitted by law. We do not charge a fee to process or respond to
                your request unless it is excessive, repetitive, or manifestly
                unfounded, in which case we will explain the reason and provide a
                cost estimate before completing the request.
            </Paragraph>

            <SectionHeading>
                Children&rsquo;s Personal Information
            </SectionHeading>
            <Paragraph>
                Our website and services are not directed to children under 16,
                and we do not knowingly sell or share the personal information of
                consumers under 16 years of age.
            </Paragraph>

            <SectionHeading>Changes to This Notice</SectionHeading>
            <Paragraph>
                We may update this Notice from time to time. When we make
                changes, we will post the updated Notice on our website and
                update the effective date.
            </Paragraph>

            <SectionHeading>Contact Information</SectionHeading>
            <Paragraph>
                Website:{' '}
                <a
                    href='https://www.ratezip.com'
                    target='_blank'
                    rel='noreferrer'
                >
                    www.ratezip.com
                </a>
                <br />
                Email:{' '}
                <a href='mailto:privacy@ratezip.com'>privacy@ratezip.com</a>
                <br />
                Postal Address: Peklava LLC dba RateZip, 59 Grove Street, Unit
                2J, New Canaan, CT 06840
            </Paragraph>
        </Wrapper>
    );
};

const Wrapper = styled.div`
    && {
        display: block;
        align-items: initial;
        justify-content: initial;
    }
    max-width: 1100px;
    width: calc(100% - 48px);
    margin: 0 auto;
    padding: 24px;
    font-family: 'Inter', sans-serif;
    color: #000000;
    line-height: 1.6;
    text-align: left;
`;

const Title = styled.h1`
    font-size: 1.75rem;
    font-weight: 700;
    color: ${theme.primary};
    margin-bottom: 4px;

    @media (max-width: ${theme.mobile}) {
        font-size: 1.375rem;
    }
`;

const Subtitle = styled.p`
    font-size: 1rem;
    font-weight: 600;
    margin-bottom: 24px;
    color: #333;
`;

const SectionHeading = styled.h2`
    font-size: 1.25rem;
    font-weight: 700;
    color: ${theme.primary};
    margin-top: 32px;
    margin-bottom: 12px;

    @media (max-width: ${theme.mobile}) {
        font-size: 1.1rem;
    }
`;

const Paragraph = styled.p`
    font-size: 1rem;
    font-weight: 400;
    line-height: 1.7;
    margin-bottom: 12px;
    color: #333;

    a {
        color: ${theme.primary};
        text-decoration: underline;
    }

    @media (max-width: ${theme.mobile}) {
        font-size: 0.875rem;
    }
`;

const TableWrapper = styled.div`
    overflow-x: auto;
    margin: 16px 0;
    border: 1px solid #ddd;
    border-radius: 4px;
`;

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
    min-width: 900px;
    font-size: 0.875rem;
    line-height: 1.5;
`;

const Th = styled.th`
    background-color: ${theme.primary};
    color: #fff;
    font-weight: 600;
    text-align: left;
    padding: 12px;
    border: 1px solid #ddd;
    font-size: 0.875rem;
`;

const Td = styled.td`
    padding: 10px 12px;
    border: 1px solid #ddd;
    vertical-align: top;
    color: #333;

    strong {
        color: #000;
    }
`;

const Bulletlist = styled.ul`
    padding-left: 24px;
    margin-bottom: 16px;

    li {
        font-size: 1rem;
        line-height: 1.7;
        margin-bottom: 8px;
        color: #333;

        a {
            color: ${theme.primary};
            text-decoration: underline;
        }

        @media (max-width: ${theme.mobile}) {
            font-size: 0.875rem;
        }
    }
`;

export default NoticeAtCollection;
